// OpenAI-compatible SSE client for Hermes. Yields structured events:
//   { type: 'content', text }                              — assistant answer delta
//   { type: 'tool', id, tool, emoji, label, status }       — live tool-call activity
//   { type: 'ping' }                                       — any other frame (heartbeat,
//                                                            role/usage chunk) — liveness only
// Hermes interleaves custom tool-activity frames (no `choices`) with the standard
// OpenAI content chunks; we surface both so the UI can show what the agent is doing
// instead of a dead "…". (Telegram/WhatsApp bridges render these same frames.)
// `ping` exists so the stall detector (doc 179 T0.7) can reset on every frame of
// any kind, not just the ones we render.
// Stage 2: baseUrl is the Daytona signed preview URL via /api/agent/hermes/session.

export async function* streamChat({ baseUrl, apiKey, conversation, sessionKey, messages, signal, model = 'gpt-5.4' }) {
  const url = `${baseUrl.replace(/\/$/, '')}/v1/chat/completions`;
  const headers = { 'Content-Type': 'application/json' };
  if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
  // Pin the Hermes session to OUR conversation id via the first-class continuity
  // header (the hermes-proxy worker now allowlists + forwards X-Hermes-Session-Id).
  // Hermes then stores the thread under this exact id, so the session list and
  // GET /api/sessions/{id}/messages line up with /agentbro/<id> — no hashing/mapping.
  // Because Hermes maintains server-side history per session, callers send only the
  // NEW turn in `messages` (verified: continuity recalls context, no duplication).
  const sid = conversation || (sessionKey ? `user-${sessionKey}-default` : undefined);
  if (sid) headers['X-Hermes-Session-Id'] = sid;

  const body = { model, stream: true, messages };

  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Hermes ${resp.status}: ${txt.slice(0, 200)}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const event = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataLine = event.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) { yield { type: 'ping' }; continue; } // comment/keepalive frame
      const payload = dataLine.slice(5).trim();
      if (payload === '[DONE]') return;
      try {
        const chunk = JSON.parse(payload);
        // Custom tool-activity frame (terminal/read_file/skill_view/…).
        if (chunk.tool) {
          yield {
            type: 'tool',
            id: chunk.toolCallId || `${chunk.tool}:${chunk.label || ''}`,
            tool: chunk.tool,
            emoji: chunk.emoji || '',
            label: chunk.label || '',
            status: chunk.status || 'running',
          };
          continue;
        }
        const delta = chunk.choices?.[0]?.delta?.content;
        if (delta) { yield { type: 'content', text: delta }; continue; }
        yield { type: 'ping' }; // role/finish/usage chunk — still proof of life
      } catch {
        yield { type: 'ping' }; // malformed/heartbeat — still proof of life
      }
    }
  }
}
